/**
 * Screen reader announcements.
 *
 * There are exactly two live regions in the whole app, declared statically in
 * index.html. Reasons this is centralised rather than per-component:
 *
 *  1. Live regions must exist in the DOM *before* the text changes, otherwise
 *     most screen readers miss the mutation. Components that mount a live
 *     region and immediately fill it announce nothing.
 *  2. Multiple simultaneous polite regions produce overlapping or dropped
 *     announcements. One queue, one region.
 *  3. A match-3 cascade can fire dozens of state changes in under a second.
 *     Announcing each one is unusable; this module coalesces.
 *
 * See docs/sdd/SDD-004-accessibility.md for the required announcement content.
 */

type Urgency = 'polite' | 'assertive';

const POLITE_ID = 'sr-announcer';
const ASSERTIVE_ID = 'sr-announcer-assertive';

/** Minimum gap between polite announcements, ms. Cascades outrun speech. */
const POLITE_THROTTLE_MS = 400;

interface Region {
  el: HTMLElement | null;
  queue: string[];
  timer: number | null;
  lastAt: number;
}

const regions: Record<Urgency, Region> = {
  polite: { el: null, queue: [], timer: null, lastAt: 0 },
  assertive: { el: null, queue: [], timer: null, lastAt: 0 },
};

function resolve(urgency: Urgency): HTMLElement | null {
  const region = regions[urgency];
  region.el ??= document.getElementById(urgency === 'polite' ? POLITE_ID : ASSERTIVE_ID);
  return region.el;
}

function flush(urgency: Urgency): void {
  const region = regions[urgency];
  region.timer = null;

  const el = resolve(urgency);
  if (!el) return;

  const message = region.queue.join('. ');
  region.queue = [];
  if (!message) return;

  /*
   * Clearing first forces a mutation even when the text is identical to the
   * previous announcement (e.g. two "Invalid move" attempts in a row), which
   * SRs otherwise ignore. The rAF gives the AT a chance to observe the empty
   * state before the new text lands.
   */
  el.textContent = '';
  requestAnimationFrame(() => {
    el.textContent = message;
  });

  region.lastAt = performance.now();
}

/**
 * Queue a message for the screen reader.
 *
 * Polite messages are coalesced: several calls inside the throttle window are
 * joined into a single utterance rather than interrupting each other. Assertive
 * messages are for things the player must hear now -- level failed, move
 * rejected -- and bypass coalescing.
 */
export function announce(message: string, urgency: Urgency = 'polite'): void {
  const text = message.trim();
  if (!text) return;

  const region = regions[urgency];
  region.queue.push(text);

  if (urgency === 'assertive') {
    if (region.timer !== null) clearTimeout(region.timer);
    flush('assertive');
    return;
  }

  if (region.timer !== null) return;

  const elapsed = performance.now() - region.lastAt;
  const delay = Math.max(0, POLITE_THROTTLE_MS - elapsed);
  region.timer = window.setTimeout(() => {
    flush('polite');
  }, delay);
}

/**
 * Drop anything queued but not yet spoken. Call on route change so the player
 * does not hear the tail of the previous screen.
 */
export function clearAnnouncements(): void {
  for (const urgency of ['polite', 'assertive'] as const) {
    const region = regions[urgency];
    if (region.timer !== null) clearTimeout(region.timer);
    region.timer = null;
    region.queue = [];
    const el = resolve(urgency);
    if (el) el.textContent = '';
  }
}
